import {
    SalaryChairBand,
    SalaryEmploymentType,
    SalaryInsuranceType,
    SalaryPayType,
    SalaryTopPriority,
    SalaryWorkDays,
    SalaryWorkHoursBand,
} from '@/api/repository/types/types';

/** 칩 하나 = 코드 + 화면 문구. 문구는 화면안(설계 PDF)과 서버 enum label 을 그대로 따른다. */
export interface ChoiceOption<T extends string> {
    code: T;
    label: string;
}

export const CHAIR_BAND_OPTIONS: ChoiceOption<SalaryChairBand>[] = [
    { code: 'LE3', label: '3개 이하' },
    { code: 'B4_6', label: '4~6개' },
    { code: 'B7_10', label: '7~10개' },
    { code: 'GE11', label: '11개 이상' },
];

export const EMPLOYMENT_TYPE_OPTIONS: ChoiceOption<SalaryEmploymentType>[] = [
    { code: 'FULL', label: '정규직' },
    { code: 'CONTRACT', label: '계약직' },
    { code: 'PART', label: '파트타임' },
];

export const WORK_DAYS_OPTIONS: ChoiceOption<SalaryWorkDays>[] = [
    { code: 'D4', label: '4일' },
    { code: 'D4_5', label: '4.5일' },
    { code: 'D5', label: '5일' },
    { code: 'D5_5_PLUS', label: '5.5일 이상' },
];

export const WORK_HOURS_BAND_OPTIONS: ChoiceOption<SalaryWorkHoursBand>[] = [
    { code: 'LE35', label: '35시간 이하' },
    { code: 'H36_40', label: '36~40시간' },
    { code: 'H41_45', label: '41~45시간' },
    { code: 'GE46', label: '46시간 이상' },
];

export const PAY_TYPE_OPTIONS: ChoiceOption<SalaryPayType>[] = [
    { code: 'ANNUAL', label: '연봉' },
    { code: 'MONTHLY', label: '월급' },
    { code: 'HOURLY', label: '시급' },
];

export const INSURANCE_OPTIONS: ChoiceOption<SalaryInsuranceType>[] = [
    { code: 'BOTH', label: '둘 다 있음' },
    { code: 'INSURANCE_ONLY', label: '4대보험만' },
    { code: 'NONE', label: '둘 다 없음' },
];

export const TOP_PRIORITY_OPTIONS: ChoiceOption<SalaryTopPriority>[] = [
    { code: 'PAY', label: '급여' },
    { code: 'HOURS', label: '근무시간' },
    { code: 'DISTANCE', label: '집과의 거리' },
    { code: 'CULTURE', label: '치과 분위기' },
    { code: 'GROWTH', label: '성장 기회' },
];

/**
 * 직군 — 서버 `UserType` enum 과 문구(description)까지 동일해야 한다.
 * 프로필 응답은 코드가 아니라 description 문자열을 주므로, 프리필 때 label 로 역매핑한다.
 */
export const USER_TYPE_OPTIONS: ChoiceOption<string>[] = [
    { code: 'DENTAL_NURSE', label: '치과위생사' },
    { code: 'DENTIST', label: '치과의사' },
    { code: 'ASSIST_NURSE', label: '간호조무사' },
    { code: 'DENTAL_TECH_OWNER', label: '치과기공사(소장)' },
    { code: 'DENTAL_TECH_WORKER', label: '치과기공사(기사)' },
    { code: 'COORDINATOR', label: '코디네이터' },
    { code: 'STEP', label: '스탭' },
    { code: 'STUDENT_DENTIST', label: '학생(치의학과)' },
    { code: 'STUDENT_DENTAL_NURSE', label: '학생(치위생과)' },
    { code: 'STUDENT_DENTAL_TECH', label: '학생(치기공과)' },
];

/**
 * 경력 구간 — 서버 `CareerBand` enum 과 코드·문구가 같아야 한다.
 * 리포트에서 3명 미만이라 서버가 빼고 내려준 구간을 "전체에만 반영"으로 채워 넣을 때 쓴다(확정 화면 7).
 */
export const CAREER_BAND_OPTIONS: ChoiceOption<string>[] = [
    { code: 'NEW', label: '1년 미만' },
    { code: 'Y1_2', label: '1~2년' },
    { code: 'Y3_4', label: '3~4년' },
    { code: 'Y5_7', label: '5~7년' },
    { code: 'Y8_PLUS', label: '8년 이상' },
];

/** 실수령을 모를 때 고르는 50만 원 단위 구간. 값은 하한(만 원)이며 서버가 중앙값(하한+25)으로 집계한다. */
export interface PayBandOption {
    /** 구간 하한 (만 원) */
    from: number;
    label: string;
}

export const PAY_BAND_OPTIONS: PayBandOption[] = [
    { from: 150, label: '150~200만' },
    { from: 200, label: '200~250만' },
    { from: 250, label: '250~300만' },
    { from: 300, label: '300~350만' },
    { from: 350, label: '350~400만' },
    { from: 400, label: '400~450만' },
    { from: 450, label: '450~500만' },
    { from: 500, label: '500만 이상' },
];

/** 직군 코드 → 화면 문구. 모르는 코드는 그대로 노출한다(서버에 새 값이 생겨도 빈칸이 되지 않게). */
export const userTypeLabel = (code: string | null | undefined): string => {
    if (!code) return '';
    return USER_TYPE_OPTIONS.find((option) => option.code === code)?.label ?? code;
};

/** 직군 문구(프로필 응답) → 코드. 못 찾으면 null. */
export const userTypeCodeOf = (label: string | null | undefined): string | null => {
    if (!label) return null;
    const matchedByLabel: ChoiceOption<string> | undefined = USER_TYPE_OPTIONS.find((option) => option.label === label);
    if (matchedByLabel) return matchedByLabel.code;
    // 이미 코드로 내려오는 경우(응답 프리필 등)도 그대로 받아들인다
    return USER_TYPE_OPTIONS.some((option) => option.code === label) ? label : null;
};

/** `2026-09` → `2026년 9월`. 형식이 다르면 원본을 그대로 돌려준다. */
export const formatCampaignMonth = (campaignKey: string | null | undefined): string => {
    if (!campaignKey) return '';
    const matched: RegExpMatchArray | null = campaignKey.match(/^(\d{4})-(\d{2})$/);
    if (!matched) return campaignKey;
    return `${matched[1]}년 ${Number(matched[2])}월`;
};

/** `2026-09` → `9월` (알림·안내 문구용) */
export const formatCampaignMonthShort = (campaignKey: string | null | undefined): string => {
    if (!campaignKey) return '';
    const matched: RegExpMatchArray | null = campaignKey.match(/^(\d{4})-(\d{2})$/);
    if (!matched) return campaignKey;
    return `${Number(matched[2])}월`;
};

/** 만 원 단위 정수 → `285만 원` */
export const formatManwon = (value: number | null | undefined): string => {
    if (value === null || value === undefined) return '-';
    return `${value.toLocaleString('ko-KR')}만 원`;
};

/** 만 원 단위 정수 → `285만` (표·차트의 짧은 표기) */
export const formatManwonShort = (value: number | null | undefined): string => {
    if (value === null || value === undefined) return '-';
    return `${value.toLocaleString('ko-KR')}만`;
};

/** 발행 시각 → `9월 30일 21시` */
export const formatPublishAt = (raw: string | null | undefined): string => {
    if (!raw) return '';
    const date: Date = new Date(raw);
    if (Number.isNaN(date.getTime())) return raw;
    return `${date.getMonth() + 1}월 ${date.getDate()}일 ${date.getHours()}시`;
};

/** 발행 시각 → `9월 30일` (시각까지 필요 없는 배지·짧은 안내용) */
export const formatPublishDate = (raw: string | null | undefined): string => {
    if (!raw) return '';
    const date: Date = new Date(raw);
    if (Number.isNaN(date.getTime())) return raw;
    return `${date.getMonth() + 1}월 ${date.getDate()}일`;
};

/** 발행 시각 → `2026.09.30` */
export const formatDate = (raw: string | null | undefined): string => {
    if (!raw) return '';
    const date: Date = new Date(raw);
    if (Number.isNaN(date.getTime())) return raw;
    const month: string = String(date.getMonth() + 1).padStart(2, '0');
    const day: string = String(date.getDate()).padStart(2, '0');
    return `${date.getFullYear()}.${month}.${day}`;
};

/** "상위 N%" 표시 — 1% 이상은 정수, 1% 미만은 소수 첫째 자리(서버가 0.1 하한 보정). */
export const formatPercentileTop = (value: number): string => {
    if (!Number.isFinite(value)) return '-';
    return value >= 1 ? `${Math.round(value)}` : value.toFixed(1);
};

/**
 * '중간 그룹'·'중간값' 설명 — 급여 화면마다 범례 바로 아래에 한 번만 둔다(2026-10-31 사용자 지시).
 * 중간 그룹 = 가운데 50%(q1~q3) 범위. 문장마다 줄바꿈(white-space: pre-line).
 */
export const MIDDLE_GROUP_EXPLAIN: string = [
    '중간 그룹: 급여 순서로 줄 세웠을 때 가운데 50%가 받는 범위예요.',
    '중간값: 급여 순서로 줄 세웠을 때 정가운데 사람이 받는 금액이에요.',
    '평균 대신 중간값을 쓰는 이유: 아주 많이 받거나 적게 받는 몇 명 때문에 숫자가 흔들리지 않아서예요.',
].join('\n');
